/**
 * Removes credential-bearing parameters from a URL before it is handed to a third party.
 *
 * Three of this app's dependencies attach the current address to what they send:
 * analytics puts it on every page view, and both error reporters attach it to every
 * report. A password-reset link carries a recovery token in its query, and the platform
 * can also deliver a session in the fragment. Either one is a credential — whoever holds
 * it can set a new password or act as the member — so neither may leave the browser.
 *
 * This is the redaction used on the telemetry side. It is deliberately separate from
 * taking the token out of the address bar: the fragment has to survive in the address bar
 * long enough for the platform's own client to read the session out of it, so it cannot
 * simply be deleted there. Redacting on the way out covers what cannot be removed at
 * source.
 *
 * Parameter names, not values, decide what goes — a value-based rule would need to
 * recognise a token by shape, and would miss a format we have not seen.
 */

/** Query parameters that carry a recovery credential. */
const CREDENTIAL_QUERY_PARAMS = ['token_hash', 'token', 'type'];

/** Fragment parameters that carry a live session. */
const CREDENTIAL_FRAGMENT_PARAMS = ['access_token', 'refresh_token', 'provider_token', 'provider_refresh_token'];

const REDACTED = 'REDACTED';

const redactParams = (raw: string, names: string[]): { value: string; changed: boolean } => {
  if (!raw) { return { value: raw, changed: false }; }

  const params = new URLSearchParams(raw);
  let changed = false;
  names.forEach((name) => {
    if (params.has(name)) {
      params.set(name, REDACTED);
      changed = true;
    }
  });

  return { value: changed ? params.toString() : raw, changed };
};

/**
 * Returns the URL with any credential parameter's value replaced by `REDACTED`.
 *
 * Keeps the parameter name so a reader can still tell a reset link apart from an ordinary
 * page view, which is most of what the URL is useful for in a report. Anything it cannot
 * parse is returned unchanged rather than guessed at — an unparseable string is not a URL
 * we can reason about, and mangling it would lose the diagnostic without gaining safety.
 */
export const redactCredentialsFromUrl = (url: string): string => {
  try {
    // A relative address is legal here (Sentry breadcrumbs carry them), so parse against
    // the current origin and put back only what came in.
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
    const parsed = new URL(url, isAbsolute ? undefined : 'http://placeholder.invalid');

    const query = redactParams(parsed.search.replace(/^\?/, ''), CREDENTIAL_QUERY_PARAMS);
    const fragment = redactParams(parsed.hash.replace(/^#/, ''), CREDENTIAL_FRAGMENT_PARAMS);

    if (!query.changed && !fragment.changed) { return url; }

    parsed.search = query.value ? `?${query.value}` : '';
    parsed.hash = fragment.value ? `#${fragment.value}` : '';

    return isAbsolute
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};
